import React from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useNavigate } from "react-router-dom";

import HeroSection from "../components/home/HeroSection";
import ResponsiveShowcase from "../components/home/ResponsiveShowcase";
import StepsSection from "../components/home/StepsSection";
import { useAuth } from "../context/AuthContext";

const WHATSAPP_URL = "https://wa.me/message/HOYXZTTFNXNZH1";

export default function HomePage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const isAuthenticated = Boolean(user);

  const continueAction = () => {
    if (loading) return;
    navigate(isAuthenticated ? "/dashboard" : "/registro");
  };

  const primaryLabel = isAuthenticated
    ? "Continuar en mi panel"
    : "Crear mi página gratis";

  return (
    <Box component="main" sx={{ width: "100%", overflowX: "hidden", bgcolor: "#fff" }}>
      <HeroSection
        onStartClick={continueAction}
        primaryLabel={primaryLabel}
        isAuthenticated={isAuthenticated}
        userName={user?.name || ""}
        onExploreClick={() => {
          document.getElementById("como-funciona")?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }}
      />

      <StepsSection
        onStartClick={continueAction}
        primaryLabel={isAuthenticated ? "Volver a mi proyecto" : "Empezar por mi cuenta"}
        isAuthenticated={isAuthenticated}
      />
      <ResponsiveShowcase
        onStartClick={continueAction}
        primaryLabel={isAuthenticated ? "Abrir mi panel" : "Crear mi proyecto ahora"}
      />

      <Box
        component="section"
        sx={{
          px: { xs: 2, md: 4 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: { xs: 5, md: 7 },
              px: { xs: 3, sm: 5, md: 8 },
              py: { xs: 5, md: 7 },
              color: "#fff",
              background:
                "linear-gradient(135deg, #19242F 0%, #243746 58%, #F35A3A 160%)",
              boxShadow: "0 28px 70px rgba(25,36,47,.22)",
              "&::before": {
                content: '\"\"',
                position: "absolute",
                width: 320,
                height: 320,
                borderRadius: "50%",
                right: -100,
                top: -150,
                background: "rgba(243,90,58,.28)",
                filter: "blur(2px)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              spacing={4}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box sx={{ maxWidth: 680 }}>
                <Typography
                  sx={{
                    color: "#FFB49F",
                    fontWeight: 900,
                    letterSpacing: ".12em",
                    textTransform: "uppercase",
                    fontSize: ".78rem",
                    mb: 1.25,
                  }}
                >
                  {isAuthenticated ? "Tu sesión sigue activa" : "Tu próximo paso está claro"}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 950,
                    letterSpacing: "-.045em",
                    lineHeight: 1.05,
                    fontSize: { xs: "2rem", sm: "2.6rem", md: "3.15rem" },
                  }}
                >
                  {isAuthenticated
                    ? "Volvé a tu panel y seguí avanzando."
                    : "Creá tu cuenta y empezá tu página hoy."}
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    color: "rgba(255,255,255,.76)",
                    fontSize: "1.05rem",
                  }}
                >
                  {isAuthenticated
                    ? "No necesitás iniciar sesión otra vez para continuar con tus proyectos, productos o configuración."
                    : "Podés avanzar por etapas, guardar los cambios y continuar cuando quieras."}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={continueAction}
                  disabled={loading}
                  startIcon={isAuthenticated ? <DashboardRoundedIcon /> : undefined}
                  endIcon={!isAuthenticated ? <ArrowForwardRoundedIcon /> : undefined}
                  sx={{
                    minHeight: 54,
                    px: 3,
                    borderRadius: 999,
                    bgcolor: "#F35A3A",
                    fontWeight: 900,
                    textTransform: "none",
                    boxShadow: "0 14px 30px rgba(243,90,58,.3)",
                    "&:hover": { bgcolor: "#DF4728" },
                  }}
                >
                  {primaryLabel}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => (window.location.href = WHATSAPP_URL)}
                  sx={{
                    minHeight: 54,
                    px: 2.5,
                    borderRadius: 999,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,.36)",
                    fontWeight: 850,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,.08)",
                    },
                  }}
                >
                  Hablar con nosotros
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
